// Company upload-first (kyc-sent): what the reader found in the uploaded company documents
// (public-extract, documents[].company), combined across documents for the client to confirm.
//
// - company fields: values that agree are merged; different values are kept as choices;
// - people: individuals merged by name (any order of names, accents ignored);
// - a company among the shareholders of any document: multi-layer ownership, no shareholders (warning only);
//   individual directors and signatories are still offered.

import formatInstitutionName from "../components/fatca/formatInstitutionName";
import { titleCaseClientName } from "./clientDocumentIntake";

export const COMPANY_READING_FIELDS = [
  { key: "name", label: "Company name" },
  { key: "country", label: "Country of registration" },
  { key: "address", label: "Registered address" },
  { key: "city", label: "City" },
  { key: "incorporationDate", label: "Date of incorporation" },
];

const clean = (value) => String(value || "").trim();
// A company name printed in capitals reads as "Naxero Digital Technology LLC" (legal-form abbreviations such as
// LLC, SA, AG or GmbH stay in capitals); names already in mixed case are kept as printed.
const inCapitals = (value) => Boolean(value) && value === value.toUpperCase() && /\p{Lu}/u.test(value);
const companyNameCase = (value) => (inCapitals(value) ? formatInstitutionName(value) : value);
// Same for people: "FABIEN JULIEN RICHARD" reads as "Fabien Julien Richard" (as for ID documents).
const personNameCase = (value) => (inCapitals(value) ? titleCaseClientName(value) : value);
const sameText = (a, b) => clean(a).normalize("NFC").toLowerCase().replace(/\s+/g, " ") === clean(b).normalize("NFC").toLowerCase().replace(/\s+/g, " ");
const personKey = (name) => clean(name).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  .split(/[^a-z0-9]+/).filter(Boolean).sort().join(" ");

export const companyReadings = (documents = []) =>
  documents.filter((doc) => doc?.status === "extracted" && doc?.category === "company_document" && doc.company);

// Exact list label the document was matched to, or "".
export const matchedDocumentLabel = (doc) => clean(doc?.company?.corporate?.matchedDocument);

// Country adjectives for the usual countries; others read "a document from {country}".
const COUNTRY_ADJECTIVES = {
  AE: "Emirati", AT: "Austrian", AU: "Australian", BE: "Belgian", BH: "Bahraini", BR: "Brazilian", CA: "Canadian",
  CH: "Swiss", CN: "Chinese", CY: "Cypriot", CZ: "Czech", DE: "German", DK: "Danish", EG: "Egyptian", ES: "Spanish",
  FI: "Finnish", FR: "French", GB: "British", GR: "Greek", HK: "Hong Kong", IE: "Irish", IL: "Israeli", IN: "Indian",
  IT: "Italian", JO: "Jordanian", JP: "Japanese", KW: "Kuwaiti", LB: "Lebanese", LU: "Luxembourg", MA: "Moroccan",
  MC: "Monegasque", MT: "Maltese", MX: "Mexican", NL: "Dutch", NO: "Norwegian", OM: "Omani", PA: "Panamanian",
  PL: "Polish", PT: "Portuguese", QA: "Qatari", RU: "Russian", SA: "Saudi", SE: "Swedish", SG: "Singaporean",
  TR: "Turkish", UA: "Ukrainian", US: "American", ZA: "South African",
};
const countryName = (iso2) => {
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(iso2) || iso2; } catch { return iso2; }
};

// Country of registration read on a document that differs from the client's (no match then), or "".
export const documentOtherCountry = (doc) => clean(doc?.company?.corporate?.documentCountry).toUpperCase();

// "This seems to be a Swiss document" / "This seems to be a document from Liechtenstein".
export function documentCountryNote(doc) {
  const iso2 = documentOtherCountry(doc);
  if (!iso2) return "";
  const adjective = COUNTRY_ADJECTIVES[iso2];
  if (!adjective) return `This seems to be a document from ${countryName(iso2)}`;
  return `This seems to be ${/^[AEIOU]/.test(adjective) ? "an" : "a"} ${adjective} document`;
}

function mergePeople(lists, fields) {
  const merged = new Map();
  for (const person of lists.flat()) {
    const key = personKey(person.name);
    if (!key) continue;
    const current = merged.get(key);
    if (!current) { merged.set(key, { ...person, name: personNameCase(clean(person.name)) }); continue; }
    for (const field of fields) if (!current[field] && person[field]) current[field] = person[field];
    if (person.roles) current.roles = [...new Set([...(current.roles || []), ...person.roles])];
    current.uncertain = current.uncertain || person.uncertain;
  }
  return [...merged.values()];
}

export function companyReadingSummary(documents = []) {
  const readings = companyReadings(documents);
  const company = Object.fromEntries(COMPANY_READING_FIELDS.map(({ key }) => {
    const options = [];
    for (const doc of readings) {
      const item = doc.company.company?.[key];
      const value = key === "name" ? companyNameCase(clean(item?.value)) : clean(item?.value);
      if (!value) continue;
      const known = options.find((option) => sameText(option.value, value));
      if (known) known.uncertain = known.uncertain && item.uncertain;
      else options.push({ value, uncertain: item.uncertain === true });
    }
    return [key, options];
  }));
  // A company among the shareholders: multi-layer ownership, completed by the client.
  const corporateShareholders = readings.some((doc) => (doc.company.warnings || []).includes("corporate_shareholders"));
  // Those companies (capitals shown in title case, legal forms kept) with their %, each once across the documents.
  const companyShareholders = [];
  for (const item of readings.flatMap((doc) => doc.company.companyShareholders || [])) {
    const name = companyNameCase(clean(item?.name));
    if (!name) continue;
    const known = companyShareholders.find((company) => company.name.toLowerCase() === name.toLowerCase());
    const country = clean(item.country).toUpperCase();
    if (!known) companyShareholders.push({ name, country, sharePercent: Number.isFinite(item.sharePercent) ? item.sharePercent : null });
    else {
      if (known.sharePercent === null && Number.isFinite(item.sharePercent)) known.sharePercent = item.sharePercent;
      if (!known.country) known.country = country;
    }
  }
  const officers = mergePeople(readings.map((doc) => doc.company.officers || []),
    ["nationality", "countryOfResidence", "dateOfBirth", "signingPower"]);
  const shareholders = mergePeople(readings.map((doc) => doc.company.shareholders || []),
    ["nationality", "countryOfResidence", "dateOfBirth", "sharePercent"]);
  const legalForm = readings.map((doc) => clean(doc.company.company?.legalForm?.value)).find(Boolean) || "";
  return {
    company,
    legalForm,
    officers,
    shareholders,
    corporateShareholders,
    companyShareholders,
    documents: readings.map((doc) => ({
      name: doc.name || "",
      matchedDocument: matchedDocumentLabel(doc),
      // "This seems to be a Swiss document" when it is from another country than the client's.
      countryNote: documentCountryNote(doc),
      issueDate: clean(doc.company.company?.issueDate?.value),
      // Address as printed when it was in another script (the form gets the English rendering).
      addressAsPrinted: clean(doc.company.addressAsPrinted),
    })),
    hasData: corporateShareholders || officers.length > 0 || shareholders.length > 0 ||
      Object.values(company).some((options) => options.length > 0),
  };
}
